import { Browser } from '@capacitor/browser';
import type { UserIdentity } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { isNative, OAUTH_REDIRECT_NATIVE, OAUTH_REDIRECT_WEB } from './platform';
import type { OAuthProvider } from './oauth';

/** The ways one account can be signed into. */
export interface SignInMethods {
  hasPassword: boolean;
  /** Connected providers as auth names them: 'email', 'google', 'apple'. */
  providers: string[];
  identities: UserIdentity[];
}

export const PROVIDER_LABEL: Record<OAuthProvider, string> = { google: 'Google', apple: 'Apple' };

/** How many separate ways the person can get in today (password counts once). */
export function methodCount(m: Pick<SignInMethods, 'hasPassword' | 'providers'>): number {
  const social = m.providers.filter((p) => p === 'google' || p === 'apple').length;
  return (m.hasPassword ? 1 : 0) + social;
}

/** A provider can be disconnected only while another way to sign in remains. */
export function canDisconnect(m: Pick<SignInMethods, 'hasPassword' | 'providers'>, provider: OAuthProvider): boolean {
  return m.providers.includes(provider) && methodCount(m) >= 2;
}

export async function loadSignInMethods(): Promise<SignInMethods> {
  const [{ data: summary, error }, { data: ids }] = await Promise.all([
    supabase.rpc('my_sign_in_methods'),
    supabase.auth.getUserIdentities(),
  ]);
  if (error) throw error;
  const s = (summary ?? {}) as { has_password?: boolean; providers?: string[] };
  return {
    hasPassword: !!s.has_password,
    providers: s.providers ?? [],
    identities: ids?.identities ?? [],
  };
}

// After connecting on the web the browser comes back through /auth/callback,
// which sends people to the app's home. Remember to bring them back to
// Settings instead. Only this fixed path is ever stored, never a URL.
const RETURN_KEY = 'sekretly.after-link';
const RETURN_PATH = '/settings';

export function takeLinkReturn(): string | null {
  try {
    const v = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    return v === RETURN_PATH ? RETURN_PATH : null;
  } catch {
    return null;
  }
}

/**
 * Attach a Google or Apple login to the account that is signed in now (rather
 * than signing in as that login). Same round trip as lib/oauth: a full-page
 * redirect on the web, the in-app browser and the deep-link handler in
 * lib/native on a phone.
 */
export async function connectProvider(provider: OAuthProvider): Promise<void> {
  if (!isNative) {
    try { sessionStorage.setItem(RETURN_KEY, RETURN_PATH); } catch { /* the return trip then lands on home */ }
  }
  const { data, error } = await supabase.auth.linkIdentity({
    provider,
    options: {
      redirectTo: isNative ? OAUTH_REDIRECT_NATIVE : OAUTH_REDIRECT_WEB,
      skipBrowserRedirect: isNative,
    },
  });
  if (error) throw error;
  if (isNative && data?.url) {
    await Browser.open({ url: data.url, presentationStyle: 'popover' });
  }
}

export async function disconnectProvider(identity: UserIdentity): Promise<void> {
  const { error } = await supabase.auth.unlinkIdentity(identity);
  if (error) throw error;
}

export async function savePassword(password: string): Promise<void> {
  const { error } = await supabase.auth.updateUser({ password });
  if (error) throw error;
}

/** Auth's linking errors, in words people understand. Null when it's something else. */
export function friendlyLinkError(err: unknown, provider?: OAuthProvider): string | null {
  const e = err as { code?: string; message?: string } | null;
  if (!e) return null;
  const name = provider ? PROVIDER_LABEL[provider] : 'That';
  if (e.code === 'identity_already_exists' || /already linked|already exists/i.test(e.message ?? '')) {
    return `${name === 'That' ? 'That login' : `That ${name} login`} already belongs to another Sekretly account, so it can't be connected here. Sign in with it directly instead, or use a different one.`;
  }
  if (e.code === 'manual_linking_disabled' || /manual linking/i.test(e.message ?? '')) {
    return "Connecting accounts isn't available right now. Please try again later.";
  }
  if (e.code === 'single_identity_not_deletable') {
    return 'You need at least one way to sign in, so the last one can\'t be removed.';
  }
  return null;
}
